import React, { useState } from "react";
import ImageCropped from "views_shared/app/javascript/components/ImageCropped";
import OutletRating from "../shared/OutletRating";
import OutletPicker from "./OutletPicker";
import RequestTypePicker from "./RequestTypePicker";

function formatEnum(value) {
  if (!value) return "";
  return value.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
}

function configBadge(config) {
  if (!config) return <span className="text-muted small fst-italic">No Config</span>;

  const colors = {
    approve: "bg-success",
    approve_and_distribute: "bg-primary",
    approve_and_notify: "bg-success",
    ghost: "bg-danger",
    manual_review: "bg-warning text-dark",
    defer: "bg-info text-dark",
  };

  const labels = { defer: "defer to outlet" };

  return (
    <span className={`badge ${colors[config.auto_action] || "bg-secondary"}`}>
      {labels[config.auto_action] || config.auto_action}
    </span>
  );
}

function profileActionDisplay(profileConfig, request) {
  if (profileConfig) return configBadge(profileConfig);
  if (request.effective_profile_action) {
    return (
      <span
        className={`badge ${request.effective_profile_action === "ghost" ? "bg-danger" : "bg-warning text-dark"}`}
        style={{ border: "1px dashed" }}
        title="Synthesized from user status"
      >
        {request.effective_profile_action.replace("_", " ")}
      </span>
    );
  }
  return <span className="small fst-italic text-muted">{request.media_outlet_id ? "None (uses outlet)" : "None"}</span>;
}

function AdminIntel({ request, fullRequest, outletConfig, profileConfig, outletDetail, onFieldSave, onTypeChange }) {
  const [showOutletPicker, setShowOutletPicker] = useState(false);
  const [showTypePicker, setShowTypePicker] = useState(false);

  const handleOutletSelect = async (outletId) => {
    await onFieldSave("media_outlet_id", outletId);
  };

  return (
    <div className="card h-100">
      <div className="card-body">
        {/* Profile section */}
        <h6 className="text-muted mb-2">Profile</h6>
        <div className="d-flex align-items-center mb-2">
          {request.profile_image_url ? (
            <ImageCropped src={request.profile_image_url} alt="" roundedCircle size={64} wrapperClasses="me-3" />
          ) : (
            <div
              className="me-3 bg-light d-flex align-items-center justify-content-center text-muted rounded-circle flex-shrink-0"
              style={{ width: 64, height: 64, fontSize: "1.25rem" }}
            >
              <i className="fa fa-user" />
            </div>
          )}
          {request.profile_slug ? (
            <a href={`/admin/users/${request.profile_slug}`} className="fw-bold text-decoration-none">
              {request.profile_name}
            </a>
          ) : (
            <strong>{request.profile_name}</strong>
          )}
        </div>

        <div className="p-2 rounded mb-2 bg-light">
          <div className="d-flex justify-content-between align-items-center">
            <span className="small text-muted">Auto Action</span>
            {profileActionDisplay(profileConfig, request)}
          </div>
        </div>

        {!profileConfig && request.effective_profile_action && (
          <p className="text-muted small fst-italic mb-0">Default — no explicit profile config set</p>
        )}
        {!profileConfig && !request.effective_profile_action && request.media_outlet_id && (
          <p className="text-muted small fst-italic mb-0">
            No profile override = falls through to outlet&apos;s auto_action
          </p>
        )}

        <hr />

        {/* Request type section */}
        <h6 className="text-muted mb-2">
          Request Type
          {onTypeChange && request.request_type !== "intro" && (
            <button
              type="button"
              className="btn btn-sm btn-link p-0 ms-2"
              onClick={() => setShowTypePicker(true)}
              title="Edit request type"
            >
              <i className="fas fa-pencil" />
            </button>
          )}
        </h6>
        <div className="mb-1">
          <div className="d-flex justify-content-between small mb-1">
            <span className="text-muted">Type</span>
            <span className="fw-medium">{formatEnum(request.request_type)}</span>
          </div>
          {request.request_sub_type && (
            <div className="d-flex justify-content-between small">
              <span className="text-muted">Sub Type</span>
              <span className="fw-medium">{formatEnum(request.request_sub_type)}</span>
            </div>
          )}
        </div>

        {showTypePicker && (
          <RequestTypePicker
            show={showTypePicker}
            onClose={() => setShowTypePicker(false)}
            onSave={onTypeChange}
            currentType={request.request_type}
            currentSubType={request.request_sub_type}
            hasLogisticsData={
              !!(fullRequest?.logistics_details?.length || fullRequest?.equipment_notes || fullRequest?.links?.length)
            }
          />
        )}

        <hr />

        {/* Outlet section */}
        <h6 className="text-muted mb-2">
          Outlet
          {onFieldSave && (
            <button
              type="button"
              className="btn btn-sm btn-link p-0 ms-2"
              onClick={() => setShowOutletPicker(true)}
              title="Change outlet"
            >
              <i className="fas fa-pencil" />
            </button>
          )}
        </h6>
        {request.media_outlet_id ? (
          <>
            <div className="mb-2">
              {request.media_outlet_profile_image_url && (
                <img
                  src={request.media_outlet_profile_image_url}
                  alt=""
                  style={{ maxWidth: 120, maxHeight: 48, objectFit: "contain" }}
                  className="d-block mb-1"
                />
              )}
              <a href={`/admin/media_outlets/${request.media_outlet_id}`} className="fw-bold text-decoration-none">
                {request.media_outlet_name}
              </a>
              {outletDetail?.domain && outletDetail.id === request.media_outlet_id && (
                <div>
                  <a
                    href={`https://${outletDetail.domain}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="small text-muted text-decoration-none"
                  >
                    <i className="fa fa-globe me-1" />
                    {outletDetail.domain}
                  </a>
                </div>
              )}
            </div>

            <div
              className={`p-2 rounded mb-2 ${outletConfig?.auto_action === "manual_review" ? "bg-warning-subtle" : "bg-light"}`}
            >
              <div className="d-flex justify-content-between align-items-center">
                <span className="small text-muted">Auto Action</span>
                {configBadge(outletConfig)}
              </div>
            </div>

            <OutletRating
              prestige={request.media_outlet_prestige}
              domainAuthority={request.media_outlet_domain_authority}
              outletClassification={request.media_outlet_classification}
              variant="large"
            />

            {request.pitch_count != null && (
              <div className="d-flex justify-content-between small mb-1">
                <span className="text-muted">Pitches</span>
                <strong>{request.pitch_count}</strong>
              </div>
            )}
          </>
        ) : (
          <div className="text-muted small fst-italic mb-2">No outlet assigned</div>
        )}

        {showOutletPicker && (
          <OutletPicker
            show={showOutletPicker}
            onClose={() => setShowOutletPicker(false)}
            onSelect={handleOutletSelect}
            currentOutletId={request.media_outlet_id}
            currentOutletName={request.media_outlet_name}
          />
        )}
      </div>
    </div>
  );
}

export default AdminIntel;
